import { BranchOption, Icon, Paragraph, Text } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useDeleteBranch } from "../../../../components/ActionGroup/Branches/useDeleteBranch";
import { useRootStore } from "../../../../stores/hooks";
import { ActionSectionWrapper, SectionHeading } from "../../style";
import type { CurrSection } from "../../types";
import {
  StyledAddBranchButton,
  StyledBlockHeaderRow,
  StyledBlockIcon,
  StyledBranchList,
  StyledFieldHelper,
  StyledFieldLabel,
  StyledTextInput,
} from "./styles";

export const BranchesBlockSection = observer<{
  instanceId: number;
  setSection: (section: CurrSection) => void;
}>(({ instanceId, setSection }) => {
  const store = useRootStore();
  const branches = store.automations.candidate.branches;
  const deleteBranchFlow = useDeleteBranch(instanceId);
  const block = branches.findBlock(instanceId);
  if (block === undefined) return null;

  const allBranches = [
    ...block.branches,
    ...(block.elseBranch !== undefined ? [block.elseBranch] : []),
  ];
  const totalBranches = allBranches.length;

  const handleAddBranch = (): void => {
    const addedHash = branches.addBranch(instanceId);
    if (addedHash !== undefined) {
      setSection({ type: "branch", instanceId, branchHash: addedHash });
    }
  };

  return (
    <ActionSectionWrapper data-testid="branches-block-section" theme={{}}>
      <SectionHeading>{t("automation-form.action-settings")}</SectionHeading>
      <StyledBlockHeaderRow>
        <StyledBlockIcon>
          {/* alt-route ships only in the icon pack's MISC bucket, so
              size="MISC" is required to load it (the default "M" has no such
              icon and renders blank); dimension sets the glyph size */}
          <Icon
            name="alt-route"
            size="MISC"
            dimension={{ width: 20, height: 20 }}
            color="currentColor"
          />
        </StyledBlockIcon>
        <Paragraph weight="semibold" size="m">
          {block.label}
        </Paragraph>
      </StyledBlockHeaderRow>

      <StyledFieldLabel htmlFor="cl-block-name">
        {t("branches.block-name-label")}
      </StyledFieldLabel>
      <StyledFieldHelper id="cl-block-name-helper">
        {t("branches.block-name-helper")}
      </StyledFieldHelper>
      <StyledTextInput
        id="cl-block-name"
        data-testid="block-name-input"
        aria-describedby="cl-block-name-helper"
        value={block.label}
        onChange={(e) => branches.setBlockLabel(instanceId, e.target.value)}
      />

      <StyledFieldLabel style={{ marginTop: 20 }}>
        {t("branches.branches-label")}
        <Text
          size="s"
          weight="semibold"
          color="error"
          styles={{ marginLeft: "2px", verticalAlign: "text-top" }}
        >
          *
        </Text>
      </StyledFieldLabel>
      <StyledBranchList>
        {allBranches.map((branch, index) => {
          const branchHash =
            block.elseBranch !== undefined && index === allBranches.length - 1
              ? "else"
              : branch.hash;
          return (
            <BranchOption
              key={branch.hash}
              label={branch.label}
              isShowDeleteIcon={
                totalBranches > store.featureFlags.limits.minTotalBranches
              }
              onClick={() =>
                setSection({ type: "branch", instanceId, branchHash })
              }
              onDelete={() => deleteBranchFlow(branchHash)}
            />
          );
        })}
      </StyledBranchList>
      <StyledAddBranchButton
        data-testid="add-branch-button"
        onClick={handleAddBranch}
        disabled={
          totalBranches >= store.featureFlags.limits.maxConditionsBranches
        }
      >
        <Icon name="plus" size="S" color="currentColor" />
        {t("branches.add-branch")}
      </StyledAddBranchButton>
    </ActionSectionWrapper>
  );
});
BranchesBlockSection.displayName = "BranchesBlockSection";
